import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import { Button } from "@/shared/components/ui/button";
import { formatCurrency } from "@/shared/lib/formatters";
import { PaymentsField } from "@/features/rentals/components/rental-create/PaymentsField";
import type { RentalPayment } from "@/features/rentals/types/rental.types";
import { step3Schema, type Step3Data } from "../../schemas";

interface WizardStep3Props {
  defaultValues?: Partial<Step3Data>;
  isEditing?: boolean;
  currentPaidValue?: number;
  /** `emitInvoice` do aluguel em edição — quando `false`, novos pagamentos não geram NF. */
  rentalEmitInvoice?: boolean | null;
  isSubmitting: boolean;
  /** Snapshot contínuo dos valores — o wizard preserva ao navegar entre passos. */
  onValuesChange?: (data: Partial<Step3Data>) => void;
  onSubmit: (data: Step3Data) => void;
  onBack: () => void;
}

export const WizardStep3 = ({
  defaultValues,
  isEditing,
  currentPaidValue,
  rentalEmitInvoice,
  isSubmitting,
  onValuesChange,
  onSubmit,
  onBack,
}: WizardStep3Props) => {
  const form = useForm<Step3Data>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      totalValue: defaultValues?.totalValue ?? 0,
      expectedOutAt: defaultValues?.expectedOutAt ?? "",
      expectedInAt: defaultValues?.expectedInAt ?? "",
      isRetroactive: defaultValues?.isRetroactive ?? false,
      outAt: defaultValues?.outAt ?? "",
      inAt: defaultValues?.inAt ?? "",
      payments: defaultValues?.payments ?? [],
      emitInvoice: defaultValues?.emitInvoice ?? true,
      invoiceObservation: defaultValues?.invoiceObservation ?? "",
    },
  });

  useEffect(() => {
    if (!onValuesChange) return;
    const subscription = form.watch((values) =>
      onValuesChange(values as Partial<Step3Data>),
    );
    return () => subscription.unsubscribe();
  }, [form, onValuesChange]);

  const payments = form.watch("payments") as RentalPayment[];
  const emitInvoice = form.watch("emitInvoice");
  const isRetroactive = form.watch("isRetroactive");
  const totalValue = form.watch("totalValue");

  // Backend exige paidValue > 0 em cada pagamento — bloqueia o submit em vez
  // de deixar o 400 estourar (mesmo padrão do dialog de retirada).
  const paymentsValid = payments.every((p) => p.paidValue > 0);

  // Resumo financeiro da edição: os pagamentos do form são NOVOS (somados
  // aos já registrados pelo backend). Não bloqueia soma > teto no front —
  // o backend valida e o erro chega via toast.
  const paidValue = currentPaidValue ?? 0;
  const newPaymentsTotal = payments.reduce(
    (sum, p) => sum + (p.paidValue || 0),
    0,
  );
  const remaining = (totalValue || 0) - paidValue - newPaymentsTotal;
  const exceedsTotal = isEditing && remaining < -0.001;
  const showEditObservation =
    !!isEditing && payments.length > 0 && rentalEmitInvoice !== false;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
        <FormField
          control={form.control}
          name="totalValue"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Valor total (R$) *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="0,00"
                  {...field}
                  onChange={(e) =>
                    field.onChange(parseFloat(e.target.value) || 0)
                  }
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="expectedOutAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data esperada de saída *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="expectedInAt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Data esperada de retorno *</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="isRetroactive"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={field.value ?? false}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      // Ao desmarcar, limpa as datas efetivas para não
                      // persisti-las por engano.
                      if (!e.target.checked) {
                        form.setValue("outAt", "");
                        form.setValue("inAt", "");
                      }
                    }}
                  />
                  <span>
                    Aluguel retroativo
                    <span className="block text-muted-foreground">
                      Marque para registrar as datas em que a peça de fato saiu
                      e retornou.
                    </span>
                  </span>
                </label>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {isRetroactive && (
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="outAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data efetiva de saída *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="inAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Data efetiva de retorno *</FormLabel>
                  <FormControl>
                    <Input type="date" {...field} value={field.value ?? ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        )}

        {isEditing ? (
          <div className="space-y-3">
            <div className="rounded-md border px-4 py-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Valor total</span>
                <span className="font-medium">
                  {formatCurrency(totalValue || 0)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Já pago</span>
                <span className="font-semibold text-green-500">
                  {formatCurrency(paidValue)}
                </span>
              </div>
              {newPaymentsTotal > 0 && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Novos pagamentos
                  </span>
                  <span className="font-medium">
                    {formatCurrency(newPaymentsTotal)}
                  </span>
                </div>
              )}
              <div className="flex justify-between border-t pt-1">
                <span className="text-muted-foreground">Restante</span>
                <span
                  className={`font-semibold ${exceedsTotal ? "text-destructive" : ""}`}
                >
                  {formatCurrency(remaining)}
                </span>
              </div>
              {exceedsTotal && (
                <p className="text-xs text-destructive">
                  Os novos pagamentos excedem o valor restante do aluguel.
                </p>
              )}
            </div>
            <PaymentsField
              label="Novos pagamentos"
              emptyMessage="Nenhum pagamento novo adicionado."
              payments={payments}
              onChange={(next) => {
                form.setValue("payments", next, { shouldDirty: true });
                // Sem pagamento novo a observação de NF seria órfã
                if (next.length === 0) {
                  form.setValue("invoiceObservation", "");
                }
              }}
            />
            <p className="text-xs text-muted-foreground">
              Pagamentos aqui adicionados são somados aos já registrados — os
              existentes não podem ser alterados ou removidos.{" "}
              {rentalEmitInvoice === false
                ? "Este aluguel foi criado sem emissão de nota fiscal; os novos pagamentos não geram nota."
                : "Cada novo pagamento gera nota fiscal."}
            </p>
          </div>
        ) : (
          <PaymentsField
            payments={payments}
            onChange={(next) =>
              form.setValue("payments", next, { shouldDirty: true })
            }
          />
        )}

        {!isEditing && (
          <FormField
            control={form.control}
            name="emitInvoice"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <label className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={field.value}
                      onChange={(e) => {
                        field.onChange(e.target.checked);
                        // Ao desmarcar, descarta a observação digitada para não
                        // persisti-la numa nota que nunca será emitida.
                        if (!e.target.checked) {
                          form.setValue("invoiceObservation", "");
                        }
                      }}
                    />
                    <span>
                      Emitir nota fiscal
                      <span className="block text-muted-foreground">
                        Marcado por padrão. Desmarque para não emitir a nota
                        fiscal deste aluguel.
                      </span>
                    </span>
                  </label>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {((!isEditing && emitInvoice) || showEditObservation) && (
          <FormField
            control={form.control}
            name="invoiceObservation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Observação da NF</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Descrição que aparece na nota fiscal"
                    rows={3}
                    maxLength={2000}
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Opcional. Em branco usa o padrão da loja.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        {!paymentsValid && (
          <p className="text-xs text-destructive">
            Informe o valor de cada pagamento antes de salvar.
          </p>
        )}

        <div className="flex justify-between pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            disabled={isSubmitting}
          >
            Voltar
          </Button>
          <Button type="submit" disabled={isSubmitting || !paymentsValid}>
            {isSubmitting ? "Salvando..." : "Salvar aluguel"}
          </Button>
        </div>
      </form>
    </Form>
  );
};
